import { groupBy, sum } from 'lib/array/arrayUtils'
import { floatify } from 'lib/maths/mathUtils'
import {
  APPLE_PAY_PAYMENT_TYPE,
  CREDIT_PAYMENT_TYPE,
  CREDIT_REVERSAL_PAYMENT_INTENT,
  GIFT_CARD_PAYMENT_TYPE,
  GOOGLE_PAY_PAYMENT_TYPE,
  LOYALTY_POINTS_PAYMENT_TYPE,
  PAYPAL_PAYMENT_TYPE,
  PROMO_PAYMENT_TYPE,
  STRIPE_CUSTOM_PAYTO_TYPE,
  STRIPE_PAYMENT_ELEMENT_APPLE_PAY_TYPE,
  STRIPE_PAYMENT_ELEMENT_CARD_TYPE,
  STRIPE_PAYMENT_ELEMENT_GOOGLE_PAY_TYPE,
  STRIPE_PAYMENT_ELEMENT_LINK_TYPE,
  STRIPE_PAYMENT_ELEMENT_TYPE,
  STRIPE_PAYMENT_ELEMENT_WALLET_TYPE,
  STRIPE_PAYMENT_TYPE,
  STRIPE_TERMINAL_PAYMENT_TYPE,
  VOUCHER_PAYMENT_TYPE,
  loyaltyBenefitPaymentTypes,
} from 'constants/payment'
import {
  getPromoPaymentSummary,
  getTotalPrice,
  getVoucherPaymentSummary,
} from 'lib/payment/getPurchaseTotals'

export function isGroupPaymentOrder(
  order: Pick<App.Order, 'groupPaymentDetails'>,
): boolean {
  return !!order.groupPaymentDetails
}

export function getMyGroupPaymentContribution(
  details: App.GroupPaymentDetails | undefined,
  customerId: string | undefined,
): App.GroupPaymentContributionDetails | undefined {
  if (!details || !customerId) {
    return undefined
  }

  return details.contributions.find(contribution => contribution.customerId === customerId)
}

export function getGroupPaymentTotalAmount(details: App.GroupPaymentDetails): number {
  return sum(
    details.contributions,
    contribution => contribution.amountPaid + contribution.amountRemaining,
  )
}

export type GroupPaymentStatusKind = 'unpaid' | 'refunded_to_lead' | 'paid_by_lead' | 'settled'

export function getGroupPaymentStatusKind(
  contribution: App.GroupPaymentContributionDetails,
): GroupPaymentStatusKind {
  if (contribution.amountRemaining > 0) {
    return 'unpaid'
  }

  if (contribution.collectedBy === 'lead' && contribution.amountRefunded > 0) {
    return 'refunded_to_lead'
  }

  if (contribution.collectedBy === 'lead') {
    return 'paid_by_lead'
  }

  return 'settled'
}

export type GroupPaymentReceiptKind = 'card' | 'paypal' | 'credits' | 'points' | 'gift_card'

export interface GroupPaymentReceiptRow {
  kind: GroupPaymentReceiptKind
  paid: number
  refunded: number
}

export interface GroupPaymentViewerReceiptTotals {
  rows: Array<GroupPaymentReceiptRow>
  paidTotal: number
  refundedTotal: number
  netPaid: number
  showRefunded: boolean
  showGiftCardCreditRefundNote: boolean
}

export interface LoyaltyBenefitPaymentLine {
  id: string
  type: App.OrderPaymentType
  amount: number
}

export interface GroupPaymentOrderTotals {
  subtotal: number
  promoTotal: number
  voucherTotal: number
  loyaltyBenefits: Array<LoyaltyBenefitPaymentLine>
  merchantFee: number
  /** Paid directly at the property; display-only, never part of any share. */
  payableAtProperty: number
  orderTotal: number
  promoCode: string | undefined
}

const RECEIPT_KIND_ORDER: Array<GroupPaymentReceiptKind> = ['card', 'gift_card', 'paypal', 'credits', 'points']

const GROUP_PAYMENT_CARD_TYPES = new Set<string>([
  STRIPE_PAYMENT_TYPE,
  STRIPE_PAYMENT_ELEMENT_TYPE,
  STRIPE_PAYMENT_ELEMENT_CARD_TYPE,
  STRIPE_PAYMENT_ELEMENT_WALLET_TYPE,
  STRIPE_PAYMENT_ELEMENT_GOOGLE_PAY_TYPE,
  STRIPE_PAYMENT_ELEMENT_APPLE_PAY_TYPE,
  STRIPE_PAYMENT_ELEMENT_LINK_TYPE,
  STRIPE_CUSTOM_PAYTO_TYPE,
  STRIPE_TERMINAL_PAYMENT_TYPE,
  APPLE_PAY_PAYMENT_TYPE,
  GOOGLE_PAY_PAYMENT_TYPE,
  'off_session_card',
])

const EMPTY_VIEWER_RECEIPTS: GroupPaymentViewerReceiptTotals = {
  rows: [],
  paidTotal: 0,
  refundedTotal: 0,
  netPaid: 0,
  showRefunded: false,
  showGiftCardCreditRefundNote: false,
}

export function getLoyaltyBenefitPaymentLines(
  payments: Array<App.OrderPayment>,
): Array<LoyaltyBenefitPaymentLine> {
  const loyaltyBenefitPayments = payments.filter(payment => (
    payment.status !== 'failed' && loyaltyBenefitPaymentTypes.has(payment.type)
  ))
  const paymentsByType = groupBy(loyaltyBenefitPayments, payment => payment.type)

  return Array.from(paymentsByType.entries()).reduce<Array<LoyaltyBenefitPaymentLine>>((lines, [type, groupedPayments]) => {
    const [firstPayment] = groupedPayments

    if (!firstPayment) {
      return lines
    }

    const amount = sum(groupedPayments, payment => payment.amount)

    if (amount === 0) {
      return lines
    }

    lines.push({
      id: groupedPayments.map(payment => payment.id).join('-'),
      type,
      amount,
    })

    return lines
  }, [])
}

const INACTIVE_ITEM_STATUSES = new Set<string>(['cancelled', 'failed'])

/**
 * Amount payable at the property for a group order: property fees plus any extra guest surcharge
 * payable at the property. Not collected by LE, so it is display-only and never part of any share.
 *
 * Prefers the order item's `propertyFees` (svc-order's "Payable at property" figure) because the
 * reservation's property fee can be 0 when the hotel's fees are set up as payable-at-property taxes.
 */
export function getGroupPaymentOrderPayableAtProperty(order: App.Order): number {
  // Skip only cancelled/failed items: straight after checkout (confirmation page) items can still be
  // pending, and they should already show what is payable at the property.
  const activeItems = order.items.filter(item => !INACTIVE_ITEM_STATUSES.has(item.status))
  const propertyFees =
    sum(activeItems, item => item.propertyFees ?? item.reservation?.propertyFees ?? 0) +
    sum(
      order.bedbankItems.filter(item => !INACTIVE_ITEM_STATUSES.has(item.status)),
      item => item.offer.packages[0].rate.totals.propertyFees ?? 0,
    )
  const extraGuestSurcharge = sum(activeItems, item => item.reservation?.extraGuestSurchargePayableAtProperty ?? 0)

  return floatify(propertyFees + extraGuestSurcharge)
}

export function getGroupPaymentOrderTotals(order: App.Order): GroupPaymentOrderTotals {
  const subtotal = getTotalPrice(order)
  const promoTotal = getPromoPaymentSummary(order.payments)
  const voucherTotal = getVoucherPaymentSummary(order.payments)
  const loyaltyBenefits = getLoyaltyBenefitPaymentLines(order.payments)
  const loyaltyBenefitTotal = sum(loyaltyBenefits, benefit => benefit.amount)
  const merchantFee = sum(order.merchantFees, fee => fee.amount)
  const promoPayment = order.payments.find(payment => payment.type === PROMO_PAYMENT_TYPE && payment.status === 'completed')
  const payableAtProperty = getGroupPaymentOrderPayableAtProperty(order)

  return {
    subtotal,
    promoTotal,
    voucherTotal,
    loyaltyBenefits,
    merchantFee,
    payableAtProperty,
    orderTotal: floatify(subtotal - promoTotal - voucherTotal - loyaltyBenefitTotal + merchantFee + payableAtProperty),
    promoCode: promoPayment?.code_name,
  }
}

function getViewerGroupPaymentReceiptKind(
  payment: App.OrderPayment,
): GroupPaymentReceiptKind | undefined {
  if (payment.status === 'failed') {
    return undefined
  }

  if (payment.intent === CREDIT_REVERSAL_PAYMENT_INTENT) {
    return undefined
  }

  if (loyaltyBenefitPaymentTypes.has(payment.type)) {
    return undefined
  }

  if (payment.type === PROMO_PAYMENT_TYPE || payment.type === VOUCHER_PAYMENT_TYPE) {
    return undefined
  }

  if (GROUP_PAYMENT_CARD_TYPES.has(payment.type)) {
    return 'card'
  }

  if (payment.type === PAYPAL_PAYMENT_TYPE) {
    return 'paypal'
  }

  if (payment.type === CREDIT_PAYMENT_TYPE) {
    return 'credits'
  }

  if (payment.type === LOYALTY_POINTS_PAYMENT_TYPE) {
    return 'points'
  }

  if (payment.type === GIFT_CARD_PAYMENT_TYPE) {
    return 'gift_card'
  }

  return undefined
}

export function getViewerGroupPaymentReceipts(
  payments: Array<App.OrderPayment>,
  memberId: string | undefined,
): GroupPaymentViewerReceiptTotals {
  if (!memberId) {
    return EMPTY_VIEWER_RECEIPTS
  }

  const paymentTotals: Record<GroupPaymentReceiptKind, number> = {
    card: 0,
    paypal: 0,
    credits: 0,
    points: 0,
    gift_card: 0,
  }
  const refundTotals: Record<GroupPaymentReceiptKind, number> = {
    card: 0,
    paypal: 0,
    credits: 0,
    points: 0,
    gift_card: 0,
  }

  payments.forEach(payment => {
    if (payment.customerId !== memberId) {
      return
    }

    const kind = getViewerGroupPaymentReceiptKind(payment)
    if (!kind) {
      return
    }

    const isRefund = payment.intent === 'refund'
    if (isRefund) {
      refundTotals[kind] += Math.abs(payment.amount)
      return
    }

    if (payment.amount > 0) {
      paymentTotals[kind] += payment.amount
    }
  })

  const rows = RECEIPT_KIND_ORDER.flatMap(kind => {
    const paid = paymentTotals[kind]
    const refunded = refundTotals[kind]
    if (paid <= 0 && refunded <= 0) {
      return []
    }

    return [{ kind, paid, refunded }]
  })
  const paidTotal = sum(rows, row => row.paid)
  const refundedTotal = sum(rows, row => row.refunded)

  return {
    rows,
    paidTotal,
    refundedTotal,
    netPaid: paidTotal - refundedTotal,
    showRefunded: refundedTotal > 0,
    showGiftCardCreditRefundNote: paymentTotals.gift_card > 0 && refundTotals.credits > 0,
  }
}
